import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Share2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { RelatedBlogPosts } from '../../../../components/Blog/RelatedBlogPosts';
import GoogleTranslateWidget from '../../../../components/GoogleTranslateWidget';
export function BlogPostNarcissismGaslightingJA() {
  const articleData = {
    title: '用語について学ぶ：ナルシシズムとガスライティング',
    description: 'ナルシシズムやガスライティングの行動に混乱していますか？テキストの主な兆候を学び、Toxic DetectorのAIが微妙な操作パターンを即座に識別する方法をご覧ください。',
    slug: 'narcissism-gaslighting-explained',
    publishDate: '2025-07-15',
    author: 'Toxic Detector Editorial'
  };
  const handleShare = async () => {
    try {
      await navigator.share({
        title: articleData.title,
        text: articleData.description,
        url: window.location.href
      });
    } catch (err) {
      navigator.clipboard.writeText(window.location.href);
      alert('リンクをクリップボードにコピーしました！');
    }
  };
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: articleData.title,
    description: articleData.description,
    image: 'https://toxicdetector.com/ja/blog/narcissism-gaslighting-explained',
    author: {
      '@type': 'Person',
      name: articleData.author
    },
    publisher: {
      '@type': 'Organization',
      name: 'ToxicDetector',
      logo: {
        '@type': 'ImageObject',
        url: 'https://toxicdetector.com/logo.png'
      }
    },
    datePublished: articleData.publishDate,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://toxicdetector.com/ja/blog/${articleData.slug}`
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-7825">
      <GoogleTranslateWidget data-id="element-7826" />
      <Helmet data-id="element-7827">
        <title data-id="element-7828">ナルシシズムとガスライティングの説明 | ToxicDetector</title>
        <meta name="description" content="ナルシシズムとガスライティングの操作戦略の背後にある心理学を理解します。" data-id="element-7829" />
        <meta property="og:image" content="https://cdn.magicpatterns.com/uploads/iLMUMmEFFWiRSqv8DSTAdz/file_00000000f0c8720a9c4e7f9e2fbdea6d.png" data-id="element-7830" />
        <meta name="keywords" content="ナルシシズム, ガスライティング, 操作, 心理学, AI分析" data-id="element-7831" />
        <link rel="canonical" href={`https://toxicdetector.com/ja/blog/${articleData.slug}`} data-id="element-7832" />
        <script type="application/ld+json" data-id="element-7833">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>

      <header className="bg-white/90 border-b border-gray-200 sticky top-0 z-50 backdrop-blur-sm" data-id="element-7834">
        <div className="max-w-3xl mx-auto px-6 py-4" data-id="element-7835">
          <div className="flex items-center justify-between" data-id="element-7836">
            <Link to="/" className="text-2xl font-bold tracking-tight text-gray-900" data-id="element-7837">
              🚩 ToxicDetector
            </Link>
            <button onClick={handleShare} className="p-2.5 hover:bg-gray-50 rounded-lg transition-colors" aria-label="Share article" data-id="element-7838">
              <Share2 size={20} className="text-gray-600" data-id="element-7839" />
            </button>
          </div>
        </div>
      </header>

      <article className="max-w-3xl mx-auto w-full px-6 py-12" data-id="element-7840">
        <Link to="/blog" className="inline-flex items-center text-gray-600 text-sm mb-10 hover:text-gray-900 transition-colors group" data-id="element-7841">
          <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-7842" />
          ブログに戻る
        </Link>

        <header className="mb-12" data-id="element-7843">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight leading-tight text-gray-900" data-id="element-7844">
            {articleData.title}
          </h1>
          <div className="flex items-center text-gray-600 text-sm gap-3" data-id="element-7845">
            <time dateTime={articleData.publishDate} data-id="element-7846">
              {new Date(articleData.publishDate).toLocaleDateString('ja-JP', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
            </time>
            <span className="text-gray-300" data-id="element-7847">•</span>
            <span data-id="element-7848">{articleData.author}</span>
          </div>
        </header>

        <div className="prose prose-lg max-w-none" data-id="element-7849">
          <p className="text-xl mb-8 leading-relaxed text-gray-700" data-id="element-7850">
            なぜ一部の人々が関係の中であなたを混乱させ、疲れさせ、自分自身を疑わせるのか疑問に思ったことはありますか？それはしばしば
            <strong data-id="element-7851">ナルシシズムとガスライティング</strong>が働いています。
          </p>

          <section className="mb-12" data-id="element-7852">
            <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-7853">
              ナルシシズムとは何ですか？
            </h2>
            <p className="text-lg leading-relaxed text-gray-600 mb-6" data-id="element-7854">
              <strong data-id="element-7855">ナルシシズム</strong>
              は、自己中心的で操作的な行動のパターンであり、他人よりも自分のニーズを優先します。
            </p>
          </section>

          <section className="mb-12" data-id="element-7856">
            <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-7857">
              ガスライティングとは何ですか？
            </h2>
            <p className="text-lg leading-relaxed text-gray-600 mb-6" data-id="element-7858">
              <strong data-id="element-7859">ガスライティング</strong>
              は、あなた自身の現実、記憶、または感情を疑わせる心理的操作技術です。
            </p>
          </section>

          <section className="mb-12" data-id="element-7860">
            <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-7861">
              AIがこれらのパターンを検出する方法
            </h2>
            <p className="text-lg leading-relaxed text-gray-600 mb-6" data-id="element-7862">
              推測する代わりに、私たちのAIは操作パターンのためにあなたの会話を分析します。
            </p>
          </section>

          <section className="mb-12" data-id="element-7863">
            <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-7864">
              これを理解することが重要な理由
            </h2>
            <p className="text-lg leading-relaxed text-gray-600" data-id="element-7865">
              <strong data-id="element-7866">ナルシシズム</strong>または
              <strong data-id="element-7867">ガスライティング</strong>
              を早期に認識することは、あなたの感情的健康を保護します。
            </p>
          </section>
        </div>

        <div className="bg-purple-600 rounded-2xl p-10 text-center text-white mt-16" data-id="element-7868">
          <h2 className="text-3xl font-bold mb-4 tracking-tight" data-id="element-7869">
            💀 真実を見る準備はできていますか？
          </h2>
          <p className="text-purple-100 mb-8 text-lg leading-relaxed max-w-2xl mx-auto" data-id="element-7870">
            推測をやめましょう。Toxic
            Detectorにテキストをアップロードして、即座に毒性スコアを取得してください。
          </p>
          <Link to="/signup" className="inline-block bg-white text-purple-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-50 transition-colors" data-id="element-7871">
            今すぐチャットをスキャン →
          </Link>
        </div>

        <div className="mt-16" data-id="element-7872">
          <RelatedBlogPosts posts={[{
          title: 'ガスライティングの7つの兆候',
          description: 'AIがテキスト会話で検出する主要なガスライティングパターンを学びます。',
          slug: '7-signs-of-gaslighting',
          emoji: '💀'
        }, {
          title: 'あなたの元は隠れたナルシシストですか？',
          description: 'テキスト分析を通じて、あなたの元が隠れたナルシシズム行動の兆候を示しているかどうかを発見してください。',
          slug: 'is-your-ex-a-covert-narcissist',
          emoji: '🎭'
        }]} data-id="element-7873" />
        </div>
      </article>

      <footer className="bg-white border-t border-gray-200 py-12 mt-16" data-id="element-7874">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-7875">
          <p className="text-gray-500 text-sm" data-id="element-7876">
            © {new Date().getFullYear()} ToxicDetector. 全著作権所有。
          </p>
        </div>
      </footer>
    </div>;
}